<template>
  <div
    class="main-menu menu-fixed menu-accordion menu-shadow"
    :class="[
      {
        expanded:
          !isVerticalMenuCollapsed ||
          (isVerticalMenuCollapsed && isMouseHovered),
      },
      skin === 'semi-dark' ? 'menu-dark' : 'menu-light',
    ]"
    @mouseenter="updateMouseHovered(true)"
    @mouseleave="updateMouseHovered(false)"
  >
    <!-- main menu header-->
    <div class="navbar-header expanded">
      <slot
        name="header"
        :toggleVerticalMenuActive="toggleVerticalMenuActive"
        :toggleCollapsed="toggleCollapsed"
        :collapseTogglerIcon="collapseTogglerIcon"
      >
        <ul class="nav navbar-nav flex-row">
          <!-- Logo & Text -->
          <li class="nav-item mr-auto">
            <b-link
              class="navbar-brand"
              to="/"
            >
              <span class="brand-logo">
                <b-img
                  :src="appLogoImage"
                  alt="logo"
                />
              </span>
              <h2 class="brand-text">
                {{ appName }}
              </h2>
            </b-link>
          </li>

          <!-- Toggler Button -->
          <li class="nav-item nav-toggle">
            <b-link class="nav-link modern-nav-toggle">
              <feather-icon
                icon="XIcon"
                size="20"
                class="d-block d-xl-none"
                @click="toggleVerticalMenuActive"
              />
              <feather-icon
                :icon="collapseTogglerIconFeather"
                size="20"
                class="d-none d-xl-block collapse-toggle-icon"
                @click="toggleCollapsed"
              />
            </b-link>
          </li>
        </ul>
      </slot>
    </div>
    <!-- / main menu header-->

    <!-- Shadow -->
    <div
      :class="{ 'd-block': shallShadowBottom }"
      class="shadow-bottom"
    />

    <!-- main menu content-->
    <vue-perfect-scrollbar
      :settings="perfectScrollbarSettings"
      class="main-menu-content scroll-area"
      tagname="ul"
      @ps-scroll-y="
        (evt) => {
          shallShadowBottom = evt.srcElement.scrollTop > 0;
        }
      "
    >
      <vertical-nav-menu-items
        :items="navMenuItems"
        class="navigation navigation-main"
      />
    </vue-perfect-scrollbar>
    <!-- /main menu content-->

    <div class="sidebar-account">
      <b-dropdown
        dropup
        no-caret
        variant="link"
        toggle-class="sidebar-account-toggle"
        menu-class="sidebar-account-menu"
      >
        <template #button-content>
          <b-avatar
            :text="
              avatarText(userData.fullName || userData.username)
            "
            variant="light-primary"
            size="30"
          />
          <div class="sidebar-account-info">
            <div class="sidebar-account-name">
              {{ userData.fullName || userData.username }}
            </div>
            <div class="sidebar-account-link">
              Ver perfil
            </div>
          </div>
          <feather-icon
            icon="ChevronRightIcon"
            size="14"
            class="sidebar-account-chevron"
          />
        </template>

        <b-dropdown-item
          :to="{ name: 'perfil' }"
          link-class="d-flex align-items-center"
        >
          <feather-icon
            size="16"
            icon="SettingsIcon"
            class="mr-50"
          />
          <span>Perfil</span>
        </b-dropdown-item>

        <b-dropdown-item
          link-class="d-flex align-items-center"
          @click="logout"
        >
          <feather-icon
            size="16"
            icon="LogOutIcon"
            class="mr-50"
          />
          <span>Salir</span>
        </b-dropdown-item>
      </b-dropdown>
    </div>
  </div>
</template>

<script>
import navMenuItems from "@/navigation/vertical"
import VuePerfectScrollbar from "vue-perfect-scrollbar"
import { BLink, BImg, BAvatar, BDropdown, BDropdownItem } from "bootstrap-vue"
import { provide, computed, ref } from "@vue/composition-api"
import useAppConfig from "@core/app-config/useAppConfig"
import { $themeConfig } from "@themeConfig"
import { avatarText } from "@core/utils/filter"
import useJwt from "@/auth/jwt/useJwt"
import { initialAbility } from "@/libs/acl/config"
import VerticalNavMenuItems from "./components/vertical-nav-menu-items/VerticalNavMenuItems.vue"
import useVerticalNavMenu from "./useVerticalNavMenu"

export default {
  components: {
    VuePerfectScrollbar,
    VerticalNavMenuItems,
    BLink,
    BImg,
    BAvatar,
    BDropdown,
    BDropdownItem,
  },
  props: {
    // La regla no la ve usada porque el consumo no está en el template ni en
    // `this`: el `props` entero se le pasa a `useVerticalNavMenu`, y ahí
    // `collapseTogglerIcon` lee `props.isVerticalMenuActive` para decidir entre
    // 'pinned' / 'unpinned' / 'close'. Borrarla dejaría el ícono del toggle
    // siempre en 'close'.
    // eslint-disable-next-line vue/no-unused-properties
    isVerticalMenuActive: {
      type: Boolean,
      required: true,
    },
    toggleVerticalMenuActive: {
      type: Function,
      required: true,
    },
  },
  setup(props) {
    const {
      isMouseHovered,
      isVerticalMenuCollapsed,
      collapseTogglerIcon,
      toggleCollapsed,
      updateMouseHovered,
    } = useVerticalNavMenu(props)

    const { skin } = useAppConfig()

    // Shadow bottom is UI specific and can be removed by user => It's not in `useVerticalNavMenu`
    const shallShadowBottom = ref(false)

    provide("isMouseHovered", isMouseHovered)

    const perfectScrollbarSettings = {
      maxScrollbarLength: 60,
      wheelPropagation: false,
    }

    const collapseTogglerIconFeather = computed(() =>
      collapseTogglerIcon.value === "unpinned" ? "CircleIcon" : "DiscIcon",
    )

    // App Name
    const { appName, appLogoImage } = $themeConfig.app

    return {
      navMenuItems,
      perfectScrollbarSettings,
      isVerticalMenuCollapsed,
      collapseTogglerIcon,
      toggleCollapsed,
      isMouseHovered,
      updateMouseHovered,
      collapseTogglerIconFeather,

      // Shadow Bottom
      shallShadowBottom,

      // Skin
      skin,

      // App Name
      appName,
      appLogoImage,
    }
  },
  data() {
    return {
      userData: JSON.parse(localStorage.getItem("userData")),
      avatarText,
    }
  },
  methods: {
    logout() {
      const token = localStorage.getItem("accessToken")

      this.$http
        .post("/auth/token-delete", {
          idusuario: this.userData.id,
          token,
        })
        .then(() => {
          localStorage.removeItem(
            useJwt.jwtConfig.storageTokenKeyName,
          )
          localStorage.removeItem(
            useJwt.jwtConfig.storageRefreshTokenKeyName,
          )

          localStorage.removeItem("userData")

          this.$ability.update(initialAbility)

          this.$router.push({ name: "auth-login" })
        })
        .catch((error) => {
          this.$swal({
            title: "Error",
            text: error.response.data.error,
            icon: "error",
            customClass: {
              confirmButton: "btn btn-primary",
            },
            buttonsStyling: false,
          })
        })
    },
  },
}
</script>

<style lang="scss">
@import "~@core/scss/base/core/menu/menu-types/vertical-menu.scss";
</style>
